import React, { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { mockData } from '../utils/mock';
import { useLanguage } from '../context/LanguageContext';
import { Reveal, Eyebrow } from './decor';
import { MenuItemCard } from './MenuItemCard';

const CATEGORY_MAP = {
  all: { es: 'Todos', en: 'All' },
  ramen: { es: 'Ramen', en: 'Ramen' },
  ramenEspecialidad: { es: 'Ramen Especialidad', en: 'Specialty Ramen' },
  combos: { es: 'Combos', en: 'Combos' },
  aperitivos: { es: 'Aperitivos', en: 'Appetizers' },
  postre: { es: 'Postre', en: 'Dessert' },
  bebidas: { es: 'Bebidas', en: 'Drinks' },
  cocteleria: { es: 'Coctelería Japonesa', en: 'Japanese Cocktails' },
  cervezasJaponesas: { es: 'Cervezas Japonesas', en: 'Japanese Beers' },
  cervezas: { es: 'Cervezas', en: 'Beers' },
  bobaRefreshers: { es: 'Boba/Refreshers', en: 'Boba/Refreshers' },
};

const CATEGORIES = ['all', 'ramen', 'ramenEspecialidad', 'combos', 'aperitivos', 'postre', 'bebidas', 'cocteleria', 'cervezasJaponesas', 'cervezas', 'bobaRefreshers'];

/** Filter the full menu by category, keeping only the first 6 items on "Todos" until expanded. */
const getVisibleMenu = (menu, selectedCategory, expanded) => {
  const isAll = selectedCategory === 'all';
  const baseList = isAll
    ? menu
    : menu.filter((item) => {
        const itemCategory = typeof item.category === 'object' ? item.category.es : item.category;
        return itemCategory === CATEGORY_MAP[selectedCategory].es;
      });
  return {
    filteredMenu: isAll && !expanded ? baseList.slice(0, 6) : baseList,
    showExpandToggle: isAll && baseList.length > 6,
  };
};

const Menu = () => {
  const { t, language } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expanded, setExpanded] = useState(false);

  const { filteredMenu, showExpandToggle } = getVisibleMenu(mockData.menu, selectedCategory, expanded);

  const selectCategory = (category) => {
    setSelectedCategory(category);
    setExpanded(false);
  };

  const loc = (v) => (typeof v === 'object' && v !== null ? v[language] || v.es : v);

  const openMenu = () => {
    window.open('https://drive.google.com/file/d/1AKEAzlXFkwbyZkO4VGh3jlxOLqN7kdHN/view?usp=sharing', '_blank');
  };

  return (
    <section id="menu" style={{ background: '#141414', padding: '100px 0' }} data-testid="menu-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-[60px]">
        {/* Header */}
        <Reveal>
          <div className="flex flex-col items-center text-center">
            <Eyebrow centered>{t('nav.menu')}</Eyebrow>
            <h2 className="font-display mt-5" style={{ fontSize: 'clamp(34px, 5vw, 52px)', fontWeight: 700, color: '#F5F5F0' }}>
              {t('menu.title')}
            </h2>
            <p className="font-body mt-4" style={{ fontSize: '15px', fontWeight: 300, color: '#C2C1B6', maxWidth: '560px', lineHeight: 1.8 }}>
              {t('menu.subtitle')}
            </p>
            <button
              onClick={openMenu}
              className="font-body uppercase mt-7 inline-flex items-center transition-colors duration-200"
              style={{ border: '1px solid #C9A84C', color: '#C9A84C', padding: '12px 26px', fontSize: '12px', letterSpacing: '2px', fontWeight: 600 }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#C9A84C'; e.currentTarget.style.color = '#0A0A0A'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#C9A84C'; }}
              data-testid="menu-view-pdf"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              {t('menu.verMenu')}
            </button>
          </div>
        </Reveal>

        {/* Filters */}
        <Reveal delay={0.1}>
          <div className="flex flex-wrap justify-center gap-3 mt-14">
            {CATEGORIES.map((category) => {
              const active = selectedCategory === category;
              return (
                <button
                  key={category}
                  onClick={() => selectCategory(category)}
                  className="font-body uppercase transition-colors duration-200"
                  style={{
                    border: `1px solid ${active ? '#C9A84C' : 'rgba(201,168,76,0.18)'}`,
                    color: active ? '#C9A84C' : '#C2C1B6',
                    background: active ? 'rgba(201,168,76,0.06)' : 'transparent',
                    padding: '9px 18px',
                    fontSize: '11px',
                    letterSpacing: '1.5px'
                  }}
                  data-testid={`menu-filter-${category}`}
                >
                  {CATEGORY_MAP[category][language]}
                </button>
              );
            })}
          </div>
        </Reveal>

        {/* Items */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {filteredMenu.map((item, index) => (
            <Reveal key={item.id} delay={Math.min(index, 5) * 0.05}>
              <MenuItemCard item={item} loc={loc} />
            </Reveal>
          ))}
        </div>

        {showExpandToggle && (
          <div className="flex justify-center mt-12">
            <button
              onClick={() => setExpanded((v) => !v)}
              className="font-body uppercase inline-flex items-center transition-colors duration-200"
              style={{ border: '1px solid #C9A84C', color: '#C9A84C', padding: '13px 30px', fontSize: '12px', letterSpacing: '2px', fontWeight: 600 }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#C9A84C'; e.currentTarget.style.color = '#0A0A0A'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#C9A84C'; }}
              data-testid="menu-expand-toggle"
            >
              {expanded ? t('menu.collapse') : t('menu.expand')}
              <span className="ml-2">{expanded ? '↑' : '↓'}</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Menu;
